import {AlertIcon, ChevronRightIcon, SearchIcon} from '@primer/octicons-react'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {Button} from '@primer/react'
import {Blankslate, SkeletonText} from '@primer/react/experimental'
import type React from 'react'

import {AIResourceResults, type AIResourceResult} from './AIResourceResults'
import styles from './AIResourceSearchPage.module.css'
import {ResourceTypeFilter, type ResourceTypeFilterProps} from './ResourceTypeFilter'
import {SearchCategoryNav, type AIResourceSearchCategory} from './SearchCategoryNav'
import {SearchResultsHeader} from './SearchResultsHeader'

export type AIResourceSearchPageState =
  | {status: 'success'; results: readonly AIResourceResult[]; totalCount?: number; nextPageHref?: string}
  | {status: 'loading'}
  | {status: 'empty'}
  | {status: 'error'}

export type AIResourceSearchPageProps = {
  categories: readonly AIResourceSearchCategory[]
  filters: ResourceTypeFilterProps
  onNextPage?: (event: React.MouseEvent) => void
  query: string
  state: AIResourceSearchPageState
}

export function AIResourceSearchPage({categories, filters, onNextPage, query, state}: AIResourceSearchPageProps) {
  return (
    <FilteredListPageLayout>
      <FilteredListPageLayout.Header>
        <FilteredListPageLayout.PageHeader>
          <FilteredListPageLayout.PageHeader.TitleArea>
            <FilteredListPageLayout.PageHeader.Title as="h1">AI resources</FilteredListPageLayout.PageHeader.Title>
          </FilteredListPageLayout.PageHeader.TitleArea>
        </FilteredListPageLayout.PageHeader>
      </FilteredListPageLayout.Header>

      <FilteredListPageLayout.Sidebar
        aria-label="Search filters"
        dialogTitle="Search filters"
        expandedWidth="medium"
        scrollable
      >
        <SearchCategoryNav categories={categories} />
        <ResourceTypeFilter {...filters} />
      </FilteredListPageLayout.Sidebar>

      <FilteredListPageLayout.Content as="div">
        <SearchResultsHeader
          query={query}
          status={state.status}
          totalCount={state.status === 'success' ? state.totalCount : undefined}
        />
        <FilteredListPageLayout.Results aria-label="AI resource results">
          <ResultsContent onNextPage={onNextPage} state={state} />
        </FilteredListPageLayout.Results>
      </FilteredListPageLayout.Content>
    </FilteredListPageLayout>
  )
}

function ResultsContent({
  onNextPage,
  state,
}: {
  onNextPage?: (event: React.MouseEvent) => void
  state: AIResourceSearchPageState
}) {
  switch (state.status) {
    case 'success':
      return <SuccessResults onNextPage={onNextPage} state={state} />
    case 'loading':
      return <LoadingResults />
    case 'empty':
      return (
        <Blankslate border spacious>
          <Blankslate.Visual>
            <SearchIcon size={24} />
          </Blankslate.Visual>
          <Blankslate.Heading as="h3">No AI resources found</Blankslate.Heading>
          <Blankslate.Description>Try a different search term.</Blankslate.Description>
        </Blankslate>
      )
    case 'error':
      return (
        <Blankslate border spacious>
          <Blankslate.Visual>
            <AlertIcon size={24} />
          </Blankslate.Visual>
          <Blankslate.Heading as="h3">AI resource search is unavailable</Blankslate.Heading>
          <Blankslate.Description>Try again later.</Blankslate.Description>
        </Blankslate>
      )
  }
}

function SuccessResults({
  onNextPage,
  state,
}: {
  onNextPage?: (event: React.MouseEvent) => void
  state: Extract<AIResourceSearchPageState, {status: 'success'}>
}) {
  const {nextPageHref} = state

  return (
    <>
      <AIResourceResults results={state.results} />
      {nextPageHref && (
        <div className={styles.Pagination}>
          <Button as="a" href={nextPageHref} onClick={onNextPage} trailingVisual={ChevronRightIcon} variant="invisible">
            Next
          </Button>
        </div>
      )}
    </>
  )
}

function LoadingResults() {
  return (
    <div className={styles.LoadingList} aria-hidden="true">
      <LoadingResultRow />
      <LoadingResultRow />
      <LoadingResultRow />
    </div>
  )
}

function LoadingResultRow() {
  return (
    <div className={styles.LoadingRow}>
      <SkeletonText size="titleMedium" />
      <SkeletonText size="bodyMedium" lines={2} />
    </div>
  )
}
